import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { type MessageKeys, type Messages, type NamespaceKeys, type NestedKeyOf, type NestedValueOf, type createTranslator,
// oxlint-disable-next-line storyteller/no-next-intl-use-translations
useTranslations } from "next-intl";
import { Children, type ReactNode } from "react";
import { FieldChip } from "@v3/_/components/field-chip";
type IntlMessages = Record<string, any>;
// taken from next-intl
type NamespacedMessageKeys<TranslatorMessages extends IntlMessages, Namespace extends NamespaceKeys<TranslatorMessages, NestedKeyOf<TranslatorMessages>> = never> = MessageKeys<NestedValueOf<{
  "!": TranslatorMessages;
}, [Namespace] extends [never] ? "!" : `!.${Namespace}`>, NestedKeyOf<NestedValueOf<{
  "!": TranslatorMessages;
}, [Namespace] extends [never] ? "!" : `!.${Namespace}`>>>;
function chunkText(chunks: ReactNode): string {
  return Children.toArray(chunks).filter((chunk): chunk is string => typeof chunk === "string").join("");
}
export const richTags = {
  em: (chunks: ReactNode) => <em>{chunks}</em>,
  field: (chunks: ReactNode) => <FieldChip field={chunkText(chunks)} />
};
function withRichTags(values: unknown) {
  return {
    ...richTags,
    ...(values as Record<string, unknown> | undefined)
  };
}
export function MissingMessageFallback(t0) {
  const $ = _c(3);
  const {
    result,
    k,
    namespace
  } = t0;
  const t1 = `Missing message: ${namespace ? `${namespace}.` : ""}${k}`;
  let t2;
  if ($[0] !== result || $[1] !== t1) {
    t2 = <span className="underline decoration-dashed" title={t1}>{result}</span>;
    $[0] = result;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function useTranslation(namespace) {
  const $ = _c(10);
  const t = useTranslations(namespace);
  let t0;
  if ($[0] !== namespace || $[1] !== t) {
    t0 = (...t1) => {
      const args = t1;
      const [key, values, formats] = args;
      const result = (t as any).rich(key, withRichTags(values), formats);
      if (typeof result === "string" && result.includes("__fallback__")) {
        const [, ...rest] = result.split("__fallback__");
        const k = args[0] as string;
        return <MissingMessageFallback namespace={namespace} k={k} result={rest.join("__fallback__")} />;
      }
      return result;
    };
    $[0] = namespace;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const fn = t0 as unknown as T;
  let t1;
  if ($[3] !== fn || $[4] !== t) {
    let t2;
    if ($[6] !== t) {
      t2 = (...t3) => {
        const args_0 = t3;
        const [key_0, values_0, formats_0] = args_0;
        return (t as any).rich(key_0, withRichTags(values_0), formats_0);
      };
      $[6] = t;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t) {
      t3 = (key_1, values_1) => (t as any)(key_1, values_1);
      $[8] = t;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t1 = Object.assign(fn, {
      rich: t2,
      markup: t.markup.bind(fn),
      raw: t.raw.bind(fn),
      has: t.has.bind(fn),
      plain: t3
    });
    $[3] = fn;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function useCommon() {
  return useTranslation("Common");
}